import React, { useState } from 'react';
import { Mail, Loader2, CheckCircle2, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { track } from '../lib/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const NewsletterSignup = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Drop your email and we\'ll handle the rest.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name: name.trim() || null, source: 'landing_page' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Subscription failed.');
      setDone(true);
      toast.success(data.message || 'You\'re in.');
      track('newsletter_signup', { source: 'landing_page' });
    } catch (err) {
      toast.error(err.message || 'Network error. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="newsletter"
      className="relative py-20 px-6 bg-gunmetal-950 border-t border-steel-800/40"
      data-testid="newsletter-section"
    >
      <div
        className="absolute inset-0 opacity-10 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 50% 0%, rgba(239,68,68,0.4) 0%, transparent 50%)',
        }}
      />

      <div className="relative max-w-3xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.4em] uppercase text-amber-400/80 mb-4">
          <span className="h-px w-10 bg-amber-400/40" />
          THE INSIDE LIST
          <span className="h-px w-10 bg-amber-400/40" />
        </div>

        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-steel-100 mb-4">
          Get the next drop <span className="text-accent-red">first.</span>
        </h2>
        <p className="text-steel-400 text-base mb-8 max-w-xl mx-auto leading-relaxed">
          New comics, new songs, tour dates, film releases — straight to your inbox before
          they hit the public feeds. No spam, no noise. One pulse, one drop at a time.
        </p>

        {done ? (
          <div
            className="inline-flex items-center gap-3 bg-gunmetal-800/60 border border-accent-red/30 rounded-lg px-6 py-5 max-w-md mx-auto"
            data-testid="newsletter-success"
          >
            <CheckCircle2 className="text-accent-red flex-shrink-0" size={24} />
            <div className="text-left">
              <div className="text-steel-100 font-bold">You're on the list.</div>
              <div className="text-steel-400 text-sm">Watch your inbox for the next MINDESIGN drop.</div>
            </div>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto"
            data-testid="newsletter-form"
          >
            <Input
              type="text"
              placeholder="Your name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="flex-1 bg-gunmetal-800 border-steel-700 text-steel-100 placeholder:text-steel-600 h-12"
              data-testid="newsletter-input-name"
            />
            <Input
              type="email"
              required
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 bg-gunmetal-800 border-steel-700 text-steel-100 placeholder:text-steel-600 h-12"
              data-testid="newsletter-input-email"
            />
            <Button
              type="submit"
              disabled={submitting}
              className="bg-accent-red hover:bg-accent-red-dark text-white font-bold tracking-wider uppercase h-12 px-6"
              data-testid="newsletter-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 animate-spin" size={16} /> Joining…
                </>
              ) : (
                <>
                  <Send className="mr-2" size={16} /> Join
                </>
              )}
            </Button>
          </form>
        )}

        <p className="text-steel-600 text-xs mt-4 flex items-center justify-center gap-1.5">
          <Mail size={11} /> Unsubscribe anytime · No spam · info@mindesignenterprises.com
        </p>
      </div>
    </section>
  );
};

export default NewsletterSignup;
